import { db } from './db.js'
import { getDocumentosStorageBytes } from './documentStorage.js'
import { formatBytes } from '@components/documentos/utils.js'

export type StorageLevel = 'ok' | 'warn' | 'critical'

export interface StorageReport {
  supported: boolean
  quotaBytes: number | null
  usageBytes: number | null
  documentosBytes: number
  documentosCount: number
  documentosComArquivo: number
  outrosBytes: number | null
  percentUsed: number | null
  level: StorageLevel
}

function levelFromPercent(percent: number | null): StorageLevel {
  if (percent == null) return 'ok'
  if (percent >= 90) return 'critical'
  if (percent >= 75) return 'warn'
  return 'ok'
}

export async function getStorageReport(): Promise<StorageReport> {
  const supported = typeof navigator !== 'undefined' && 'storage' in navigator
  let quotaBytes: number | null = null
  let usageBytes: number | null = null

  if (supported && navigator.storage.estimate) {
    try {
      const estimate = await navigator.storage.estimate()
      quotaBytes = estimate.quota ?? null
      usageBytes = estimate.usage ?? null
    } catch {
      /* noop */
    }
  }

  const docs = await db.documentos.toArray()
  const documentosBytes = await getDocumentosStorageBytes()
  const documentosComArquivo = docs.filter((d) => d.hasArquivo).length

  const percentUsed =
    quotaBytes && usageBytes != null
      ? Math.min(100, Math.round((usageBytes / quotaBytes) * 100))
      : null

  return {
    supported,
    quotaBytes,
    usageBytes,
    documentosBytes,
    documentosCount: docs.length,
    documentosComArquivo,
    outrosBytes: usageBytes != null ? Math.max(0, usageBytes - documentosBytes) : null,
    percentUsed,
    level: levelFromPercent(percentUsed),
  }
}

export function storageLevelMessage(level: StorageLevel): string | null {
  if (level === 'critical') {
    return 'Espaço quase esgotado. Exporte um backup e remova arquivos antigos.'
  }
  if (level === 'warn') {
    return 'Uso elevado. Considere exportar backup e revisar documentos grandes.'
  }
  return null
}

export { formatBytes }
